/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Component, OnInit, OnDestroy, ErrorHandler } from '@angular/core';
import { trigger, state, animate, transition, style } from '@angular/animations';
import { Router, ActivatedRoute, NavigationStart, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { CommonService } from './shared/services/common.service';
import { HomeService } from './components/home/home.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LoaderService } from './components/loader/loader.service';
import { ToastrService } from 'ngx-toastr';
import { BookmarkSliderService } from './shared/services/slider.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('panelVisibility', [
      state('open', style({
        width: '24rem'
      })),
      state('collapse', style({
        width: '4.8rem'
      })),
      state('hide', style({
        width: '0rem'
      })),
      transition('open => collapse', [animate('.2s ease-in-out', style({ width: '4.8rem' }))]),
      transition('collapse => open', [animate('.2s ease-in-out', style({ width: '24rem' }))]),
      transition('open => hide', [animate('.2s ease-in-out', style({ width: '0rem' }))]),
      transition('hide => open', [animate('.2s ease-in-out', style({ width: '24rem' }))]),
      transition('collapse => hide', [animate('.2s ease-in-out', style({ width: '0rem' }))]),
      transition('hide => collapse', [animate('.2s ease-in-out', style({ width: '4.8rem' }))])
    ]),
    trigger('contentVisibility', [
      state('open', style({
        transform: 'translateX(19rem)'
      })),
      state('collapse', style({
        transform: 'translateX(0rem)'
      })),
      state('hide', style({
        transform: 'translateX(-4.8rem)'
      })),
      transition('open => collapse', [animate('.2s ease-in-out', style({ transform: 'translateX(0rem)' }))]),
      transition('collapse => open', [animate('.2s ease-in-out', style({ transform: 'translateX(19rem)' }))]),
      transition('open => hide', [animate('.2s ease-in-out', style({ transform: 'translateX(-4.8rem)' }))]),
      transition('hide => open', [animate('.2s ease-in-out', style({ transform: 'translateX(19rem)' }))]),
      transition('collapse => hide', [animate('.2s ease-in-out', style({ transform: 'translateX(-4.8rem)' }))]),
      transition('hide => collapse', [animate('.2s ease-in-out', style({ transform: 'translateX(0rem)' }))])
    ]),
    trigger('panelSlider', [
      state('show', style({
        'right': '0'
      })),
      state('hide', style({
        'right': '-56rem'
      })),
      transition('show => hide', [animate('.2s linear', style({ 'right': '-56rem' }))]),
      transition('hide => show', [animate('.2s linear', style({ 'right': '0' }))])
    ]),
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateX(0)',
        "visibility": "visible"
      })),
      state('out', style({
        transform: 'translateX(100%)',
        "visibility": "hidden"
      })),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out'))
    ])
  ]
})
export class AppComponent implements OnInit, OnDestroy, ErrorHandler {
  public appRoutes: any = '';
  public sidePanel = false;
  public panelState = 'collapse';
  public showSponsorSlide = 'out';
  public showEnrollerSlide = 'out';
  public showVolumeTransferSide = 'out';
  public showbookmarksSlide = 'out';
  public showRecentlyViewedSlide = 'out';
  public showMoveOrderSlide = 'out';
  public showOrganizationSlide = 'out';
  public showParentOrganizationSlide = 'out';
  public showOrganizationDownlineSlide = 'out';
  private subscription!: Subscription;
  private transSubscription!: Subscription;
  public participant: any;
  public transLoad: any = false;
  public showreportsBookmarksSlide: any;

  constructor(private router: Router,
    private route: ActivatedRoute,
    private bookmarkSliderService: BookmarkSliderService,
    location: Location,
    private translate: TranslateService,
    private commonService: CommonService,
    private homeService: HomeService,
    private toastr: ToastrService,
    private loaderService: LoaderService) {
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang(localStorage.getItem('userLanguage') || 'en_us');
    this.transLoad = localStorage.setItem('transLoad', "false");
    this.commonService.loadTranslations();
    const userLanguage = localStorage.getItem('userLanguage');
    const language =
      !userLanguage || userLanguage === 'null' || userLanguage === 'undefined'
        ? 'en_US'
        : userLanguage;

    this.commonService.getTranslationsOfSelectedLanguage(language).then(
      (res: any) => {
        this.commonService.setTranslatedObj(res);
      },
      (err) => {
        this.commonService.commonErrorHandler(err);
        this.commonService.setTranslatedObj([]);
      }
    );

    this.router.events.subscribe((val) => {
      this.appRoutes = location.path();
      if (val instanceof NavigationStart) {
        this.toggleSponsorSlide('close');
        this.toggleEnrollerSlide('close');
        this.toggleBookmarksSlide('close');
        this.toggleVolumeTransferSlide('close');
        this.toggleRecentlyViewedSlide('close');
        this.toggleMoveOrderSlide('close');
        this.toggleOrganizationSlide('close');
        this.toggleParentOrganizationSlide('close');
        this.toggleOrganizationDownlineSlide('close');
      }
    });
  }

  ngOnInit() {
    this.subscription = this.bookmarkSliderService.showbookmarksSlide$
      .subscribe(state => {
        this.showreportsBookmarksSlide = state;
      });

    this.subscription = this.commonService.getPanelState().subscribe(panelState => {
      this.displaySidePanel(panelState);
    });
    this.commonService.menuClicked.subscribe(panel => {
      this.selectedMenuItem(panel.name);
      this.participant = panel.clientInfo;
    });
    this.commonService.panelBtnClicked.subscribe(panel => {
      this.selectedMenuItem(panel.name);
    });

    this.transSubscription = this.commonService.getTransState().subscribe(transState => {
      this.transLoad = transState;
    });

    this.router.errorHandler = function () {
      // Handle here. err.message == "Loading chunk chunk-name failed."
      window.location.reload();
    };
    // logic to show/hide loader during Lazy loading of modules
    this.router.events.subscribe(event => {
      const evt: any = event;
      this.loaderService.hideLoader();
      if (event instanceof NavigationStart && evt.url && !evt.url.toString().match('unjustEnrichment')) {
        this.loaderService.setPageLoad(true);
      }
      if (evt.url && evt.url.toString().match('planwizard')) {
        this.loaderService.setPageLoad(false);
        this.loaderService.showLoader();
      }
      if (event instanceof NavigationStart) {
        this.toastr.clear();
      }
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          this.loaderService.setPageLoad(false);
        }, 3000);
      }
    });
  }

  ngOnDestroy() {
    // unsubscribe to ensure no memory leaks
    this.subscription.unsubscribe();
    this.transSubscription.unsubscribe();
  }

  public handleError(error: any) {
    console.error('Global error:', error);
  }

  public displaySidePanel(visible: any) {
    if (visible == 'hide') {
      this.panelState = visible;
    } else {
      this.sidePanel = visible;
      this.panelState = (this.sidePanel) ? 'open' : 'collapse';
    }
  }

  public selectedMenuItem(event: any) {
    // logic to collapse side menu after navigation
    //this.panelState = (screen.width > 767) ? 'collapse' : 'hide';
    this.commonService.closeDDSliders();
    this.toggleBookmarksSlide('close');
    this.toggleSponsorSlide('close');
    this.toggleEnrollerSlide('close');
    this.toggleVolumeTransferSlide('close');
    this.toggleRecentlyViewedSlide('close');
    this.toggleMoveOrderSlide('close');
    this.toggleOrganizationSlide('close');
    this.toggleParentOrganizationSlide('close');
    this.toggleOrganizationDownlineSlide('close');
    this.participant = ''; /* Clearing sponsor info on menuitem selected */

    // this.translate.get("permissions.menus").subscribe(menuTransObj => {
      if (event === this.getTranslationWithKey("CSR")) { /* Change Sponsor */
        this.toggleSponsorSlide();
      }
      if (event === this.getTranslationWithKey('CER')) { /* Change Enroller */
        this.toggleEnrollerSlide();
      }
      if (event === this.getTranslationWithKey('BKM')) { /* Bookmarks */
        this.toggleBookmarksSlide();
      } 
      else if (event === this.getTranslationWithKey('TRF')) { /* Volume Transfer */
        this.toggleVolumeTransferSlide();
      } else if (event === this.getTranslationWithKey('RCV')) { /* Recently Viewed */
        this.toggleRecentlyViewedSlide();
      } else if (event === this.getTranslationWithKey('MOVR')) {
        this.toggleMoveOrderSlide();
      } else if (event === this.getTranslationWithKey('COR')) { /* Change Organization */
        this.toggleOrganizationSlide();
      } else if (event === this.getTranslationWithKey('CPR')) {  /*Change parent Organization */
        this.toggleParentOrganizationSlide();
      } else if (event === this.getTranslationWithKey('COD')) {  /*Change Organization Unit Downline */
        this.toggleOrganizationDownlineSlide();
      } 

      if ((event === this.getTranslationWithKey('RCV')) || (event === this.getTranslationWithKey('BKM'))) {
        setTimeout(() => {
          this.homeService.homeMetaData.emit();
        }, 300);
      }
  }

  public getTranslationWithKey(key: string): string {
    return this.commonService.getTranslatedVal(key);
  }

  public toggleSponsorSlide(event?: any) {
    if (!event) {
      this.showSponsorSlide = (this.showSponsorSlide === 'in' ? 'out' : 'in');
    } else {
      this.showSponsorSlide = 'out';
    }
  }

  public toggleEnrollerSlide(event?: any) {
    if (!event) {
      this.showEnrollerSlide = (this.showEnrollerSlide === 'in' ? 'out' : 'in');
    } else {
      this.showEnrollerSlide = 'out';
    }
  }

  public toggleBookmarksSlide(event?: any) {
    if (!event) {
      this.showbookmarksSlide = (this.showbookmarksSlide === 'in' ? 'out' : 'in');
    } else {
      this.showbookmarksSlide = 'out';
    }
    // trigger bookmark slider close 
    if (!event && this.showbookmarksSlide == 'out') {
      this.commonService.bookmarkPanelClose();
    }
  }

  public toggleVolumeTransferSlide(event?: any) {
    const volTransferLbl: any = this.translate.get("permissions.menus.TRF");
    if (!event) {
      this.showVolumeTransferSide = (this.showVolumeTransferSide === 'in' ? 'out' : 'in');
    } else {
      this.showVolumeTransferSide = 'out';
    }
    if (this.showVolumeTransferSide == 'out' && event == undefined && this.appRoutes.match('/fieldsupport/distributorDetails/')) {
      this.commonService.setReprocess(false);
      this.commonService.navigatePanelClose(volTransferLbl.value);
    }
  }

  public toggleRecentlyViewedSlide(event?: any) {
    if (!event) {
      this.showRecentlyViewedSlide = (this.showRecentlyViewedSlide === 'in' ? 'out' : 'in');
    } else {
      this.showRecentlyViewedSlide = 'out';
    }
  }
  public toggleMoveOrderSlide(event?: any) {
    const movOrdersLbl: any = this.translate.get("permissions.menus.MOVR");
    if (!event) {
      this.showMoveOrderSlide = (this.showMoveOrderSlide === 'in' ? 'out' : 'in');
    } else {
      this.showMoveOrderSlide = 'out';
    }
    if (this.showVolumeTransferSide == 'out' && event == undefined && this.appRoutes.match('/fieldsupport/distributorDetails/')) {
      this.commonService.setReprocess(false);
      this.commonService.navigatePanelClose(movOrdersLbl.value);
    }
  }

  public toggleOrganizationSlide(event?: any) {
    if (!event) {
      this.showOrganizationSlide = (this.showOrganizationSlide === 'in' ? 'out' : 'in');
    } else {
      this.showOrganizationSlide = 'out';
    }
  }

  public toggleParentOrganizationSlide(event?: any) {
    if (!event) {
      this.showParentOrganizationSlide = (this.showParentOrganizationSlide === 'in' ? 'out' : 'in');
    } else {
      this.showParentOrganizationSlide = 'out';
    }
  }

  public toggleOrganizationDownlineSlide(event?: any) {
    if (!event) {
      this.showOrganizationDownlineSlide = (this.showOrganizationDownlineSlide === 'in' ? 'out' : 'in');
    } else {
      this.showOrganizationDownlineSlide = 'out';
    }
  }

  public evtStopPropagation(evt: any) {
    if (evt.target.className !== 'close-icon') {
      evt.stopPropagation();
    }
  }

  public isAnySliderVisible() {
    if (this.showSponsorSlide == 'in' || this.showbookmarksSlide == 'in'
      || this.showVolumeTransferSide == 'in'|| this.showRecentlyViewedSlide == 'in' 
      || this.showMoveOrderSlide == 'in' || this.showEnrollerSlide == 'in' || this.showOrganizationSlide == 'in'
      || this.showParentOrganizationSlide == 'in' || this.showOrganizationDownlineSlide == 'in' ) {
      return true;
    } else {
      return false;
    }
  }

  public closeAllSliders() {
    if (this.isAnySliderVisible()) {
      switch ('in') {
        case this.showSponsorSlide:
          this.toggleSponsorSlide();
          break;
        case this.showbookmarksSlide:
          this.toggleBookmarksSlide();
          break;
        case this.showVolumeTransferSide:
          this.toggleVolumeTransferSlide();
          break;
        case this.showRecentlyViewedSlide:
          this.toggleRecentlyViewedSlide();
          break;
        case this.showMoveOrderSlide:
          this.toggleMoveOrderSlide();
          break;
        case this.showOrganizationSlide:
          this.toggleOrganizationSlide();
          break;
        case this.showParentOrganizationSlide:
          this.toggleParentOrganizationSlide();
          break;
        case this.showOrganizationDownlineSlide:
          this.toggleOrganizationDownlineSlide();
          break;
        default:
          this.toggleEnrollerSlide();
      }
    }
  }

  public toggleReportBookmarksSlide(event?: any) {
    if (!event) {
      this.bookmarkSliderService.toggle();
    } else {
      this.bookmarkSliderService.hide();
    }
  }
}
